'use client';

import { useEffect, useMemo, useState } from 'react';
import type { ConsultService, Palette } from './types';


type GuidedProps = {
  id?: string;
  palette: Palette;
  consultServices: readonly ConsultService[];
  selectedServices: string[];
  onSelectPrimary: (key: string) => void;
  onToggleService: (key: string) => void;
  onFinish?: (data: any) => void; // Optional callback if parent needs to know
};

const STEP_COUNT = 3;
const HOME_PROS_ADDRESS_KEY = 'homepros_quote_address';
const PLAN_CHECKLIST_ITEMS = [
  'Service-specific signs to watch for',
  'What each selected service is best for',
  'What to prepare before your consult',
] as const;
const PLAN_SERVICE_OPTIONS: ConsultService[] = [
  { key: 'batteries', label: 'Batteries' },
  { key: 'electrical_upgrades', label: 'Electrical Upgrades' },
  { key: 'generators', label: 'Generators' },
  { key: 'permanent_home_lighting', label: 'Permanent Home Lighting' },
  { key: 'asphalt_roofing', label: 'Asphalt Roofing' },
  { key: 'solar_panels', label: 'Solar Panels' },
  { key: 'solar_repair', label: 'Solar Repair' },
  { key: 'water_softener', label: 'Water Softener' },
  { key: 'other', label: 'Other' },
];
const newEventId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `evt_${Date.now()}_${Math.random().toString(36).slice(2)}`;

export function GuidedFormSection({
  id,
  palette,
  consultServices,
  selectedServices,
  onSelectPrimary,
  onToggleService,
}: GuidedProps) {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
  });

  const primary = selectedServices[0] ?? null;
  const hasSelection = selectedServices.length > 0;

  const planServices = useMemo(() => PLAN_SERVICE_OPTIONS, []);

  const addableServices = useMemo(
    () => planServices.filter((svc) => svc.key !== primary),
    [planServices, primary]
  );
  const getServiceLabel = (key: string) => {
    const svc = planServices.find((c) => c.key === key);
    const raw = svc?.label || key;
    return raw;
  };

  const progress = Math.round((step / STEP_COUNT) * 100);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const savedAddress = window.localStorage.getItem(HOME_PROS_ADDRESS_KEY);
    if (!savedAddress) return;
    setFormData((prev) => ({ ...prev, address: savedAddress }));
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!formData.address.trim()) {
      window.localStorage.removeItem(HOME_PROS_ADDRESS_KEY);
      return;
    }
    window.localStorage.setItem(HOME_PROS_ADDRESS_KEY, formData.address);
  }, [formData.address]);

  // Navigation Logic
  const goToStep = (next: number) => {
    setStep(next);
  };

  // API Submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const eventId = newEventId();
      if (typeof window !== 'undefined' && typeof (window as any).fbq === 'function') {
        (window as any).fbq('track', 'Lead', {}, { eventID: eventId });
      }

      const payload = {
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        preferred: 'Email', // Defaulting since we are sending a checklist
        bestTime: 'Anytime',
        brand: 'homepros', // Identifier for your logic
        brandLabel: 'Home Pros Texas',
        // We pack the selected services into the notes so the rep knows what checklist to send
        notes: `Address: ${formData.address || '(not provided)'}\nUser requested Project Checklist for: ${selectedServices
          .map((s) => getServiceLabel(s))
          .join(', ')}\nChecklist Includes:\n- ${PLAN_CHECKLIST_ITEMS.join('\n- ')}`,
        selectedServices,
        selectedServiceLabels: selectedServices.map((s) => getServiceLabel(s)),
        pageUrl: window.location.href,
        eventId,
        metaEventName: 'Lead',
      };

      const res = await fetch('/api/send-contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setIsSuccess(true);
      } else {
        alert('Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error(err);
      alert('Error sending request.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render Success State
  if (isSuccess) {
    return (
      <section id={id} className="px-6 py-20 lg:px-12" style={{ backgroundColor: '#F0F4F2' }}>
        <div className="mx-auto max-w-2xl rounded-[40px] border border-emerald-200 bg-white p-12 text-center shadow-xl">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
            <svg className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-3xl font-bold text-slate-900">Checklist Sent!</h3>
          <p className="mt-4 text-lg text-slate-600">
            We've sent the <strong>{selectedServices.length > 1 ? 'Multi-Trade' : 'Project'} Planning Guide</strong> to {formData.email}.
          </p>
          <p className="mt-2 text-sm text-slate-500">
            Please check your spam folder if you don't see it within 5 minutes.
          </p>
          <button 
             onClick={() => window.location.reload()}
             className="mt-8 text-sm font-bold uppercase tracking-widest text-emerald-600 hover:text-emerald-800"
          >
            Start New Plan
          </button>
        </div>
      </section>
    );
  }

  return (
    <section
      id={id}
      className="relative overflow-hidden px-6 py-20 scroll-mt-24 lg:px-12"
      style={{ backgroundColor: '#F0F4F2' }}
    >
      {/* --- BACKGROUND BLOBS --- */}
      <div 
        className="absolute left-0 bottom-0 h-[600px] w-[600px] -translate-x-1/3 translate-y-1/3 rounded-full opacity-30 blur-[100px]"
        style={{ background: palette.sage }}
      />
      <div 
        className="absolute right-0 top-0 h-[500px] w-[500px] translate-x-1/3 -translate-y-1/3 rounded-full opacity-20 blur-[80px]"
        style={{ background: palette.clay }}
      />

      <div
        className="relative mx-auto flex max-w-7xl flex-col gap-8 rounded-[40px] border border-white/60 bg-white/60 p-8 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.1)] backdrop-blur-xl lg:p-12"
      >
        {/* HEADER */}
        <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-emerald-800">
               <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
               Start Smart
            </div>
            <h3 className="mt-3 text-2xl font-bold text-slate-900 sm:text-3xl">Let's build your plan.</h3>
            <p className="mt-2 text-base text-slate-600">
              Build your scope below and we'll email you a tailored checklist of what to expect.
            </p>
          </div>
          
          {/* Progress Indicator */}
          <div className="flex flex-col items-end gap-2">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Step {step} of {STEP_COUNT}</span>
            <div className="flex items-center gap-3">
               <div className="h-2 w-32 sm:w-48 overflow-hidden rounded-full bg-slate-200">
                 <div
                    className="h-full rounded-full transition-all duration-700 ease-out shadow-[0_0_10px_rgba(16,185,129,0.4)]"
                    style={{
                      width: `${progress}%`,
                      background: `linear-gradient(90deg, ${palette.sage}, ${palette.forest})`,
                    }}
                  />
               </div>
            </div>
          </div>
        </div>

        {/* LAYOUT LOGIC: 
          If hasSelection is FALSE, we center the content and hide the right column.
          If hasSelection is TRUE, we show the grid.
        */}
        <div className={`grid gap-8 transition-all duration-500 ${hasSelection ? 'lg:grid-cols-[2fr,1fr]' : 'lg:grid-cols-1 max-w-3xl mx-auto w-full'}`}>
          
          {/* LEFT: INTERACTIVE STEPS */}
          <div className="rounded-3xl border border-white/50 bg-white/50 p-6 shadow-sm backdrop-blur-sm sm:p-8">
            
            {/* STEP 1: PRIMARY */}
            {step === 1 && (
              <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="flex items-center justify-between">
                   <h4 className="text-xl font-bold text-slate-900">Which services do you want?</h4>
                </div>
                
                <div className="grid gap-4 sm:grid-cols-2">
                  {planServices.map((service) => {
                    const active = selectedServices.includes(service.key);
                    const serviceLabel = getServiceLabel(service.key);
                    return (
                      <button
                        key={service.key}
                        type="button"
                        onClick={() => onToggleService(service.key)}
                        className={`group relative flex items-center justify-between rounded-2xl border p-5 text-left transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${
                          active
                            ? 'border-amber-200 bg-amber-50 shadow-md ring-1 ring-amber-200'
                            : 'border-slate-200 bg-white hover:border-amber-200 hover:bg-white'
                        }`}
                      >
                        <span className={`text-base font-bold ${active ? 'text-slate-900' : 'text-slate-700 group-hover:text-slate-900'}`}>
                          {serviceLabel}
                        </span>
                        
                        <div className={`h-6 w-6 rounded-full border-2 flex items-center justify-center transition-colors ${active ? 'border-amber-500 bg-amber-500' : 'border-slate-300 bg-transparent group-hover:border-amber-300'}`}>
                           {active && <div className="h-2.5 w-2.5 rounded-full bg-white" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
                
                <div className="flex items-center justify-end pt-6 border-t border-slate-100">
                  <button
                    type="button"
                    disabled={!selectedServices.length}
                    onClick={() => goToStep(2)}
                    className="group relative inline-flex items-center gap-2 rounded-full px-8 py-3 text-xs font-bold uppercase tracking-[0.25em] text-white shadow-lg transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-emerald-900/20 disabled:opacity-50 disabled:shadow-none disabled:hover:translate-y-0"
                    style={{ background: palette.forest }}
                  >
                    Next Step
                    <span className="transition-transform group-hover:translate-x-1">→</span>
                  </button>
                </div>
              </div>
            )}

            {/* STEP 2: MULTI-SELECT ADD-ONS */}
            {step === 2 && (
              <div className="space-y-6 animate-in fade-in slide-in-from-right-8 duration-500">
                <div className="flex items-center justify-between">
                   <div>
                     <h4 className="text-xl font-bold text-slate-900">Add other services?</h4>
                     <p className="text-sm text-slate-500">Bundling saves time and project fees.</p>
                   </div>
                </div>
                
                <div className="grid gap-4 sm:grid-cols-2">
                  {addableServices.map((service) => {
                    const active = selectedServices.includes(service.key);
                    const serviceLabel = getServiceLabel(service.key);
                    return (
                      <button
                        key={service.key}
                        type="button"
                        onClick={() => onToggleService(service.key)}
                        className={`group relative flex items-center justify-between rounded-2xl border p-5 text-left transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${
                          active
                            ? 'border-emerald-200 bg-emerald-50 shadow-sm ring-1 ring-emerald-200'
                            : 'border-slate-200 bg-white hover:border-emerald-200 hover:bg-white'
                        }`}
                      >
                         <span className={`text-base font-bold ${active ? 'text-emerald-900' : 'text-slate-700 group-hover:text-slate-900'}`}>
                          {serviceLabel}
                        </span>
                        
                        <div className={`flex h-6 w-6 items-center justify-center rounded-lg border-2 transition-all ${active ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300 bg-white group-hover:border-emerald-300'}`}>
                          {active && (
                            <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
                
                <div className="flex items-center justify-between pt-6 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => goToStep(1)}
                    className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    ← Back
                  </button>
                  <button
                    type="button"
                    onClick={() => goToStep(3)}
                    className="group relative inline-flex items-center gap-2 rounded-full px-8 py-3 text-xs font-bold uppercase tracking-[0.25em] text-white shadow-lg transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-amber-500/20"
                    style={{ background: palette.clay }}
                  >
                    Continue
                    <span className="transition-transform group-hover:translate-x-1">→</span>
                  </button>
                </div>
              </div>
            )}

            {/* STEP 3: CONTACT FORM (Replaces Scheduler) */}
            {step === 3 && (
              <form onSubmit={handleSubmit} className="space-y-6 animate-in fade-in slide-in-from-right-8 duration-500">
                <div className="flex items-center justify-between">
                   <div>
                     <h4 className="text-xl font-bold text-slate-900">Where should we send your plan?</h4>
                     <p className="text-sm text-slate-500">We'll email you the checklist for your specific scope.</p>
                   </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500">
                      Full Name
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                      placeholder="Jane Doe"
                    />
                  </div>
                  
                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500">
                      Email Address
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                      placeholder="jane@example.com"
                    />
                  </div>
                  
                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500">
                      Project Address
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                      placeholder="Street address, city, state"
                      autoComplete="street-address"
                    />
                    <p className="mt-1 text-[11px] text-slate-500">
                      Saved automatically while you type.
                    </p>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500">
                      Phone (Optional)
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                      placeholder="(210) 555-0123"
                    />
                  </div>
                </div>
                
                <div className="flex items-center justify-between pt-6 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => goToStep(2)}
                    className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    ← Back
                  </button>
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="group relative inline-flex items-center gap-2 rounded-full px-8 py-3 text-xs font-bold uppercase tracking-[0.25em] text-white shadow-lg transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-amber-500/20 disabled:opacity-50 disabled:shadow-none disabled:translate-y-0"
                    style={{ 
                      background: `linear-gradient(to right, ${palette.clay}, #b45309)` 
                    }}
                  >
                    {isSubmitting ? <LoadingDots /> : 'Get Checklist'}
                    {!isSubmitting && <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent group-hover:animate-shimmer" />}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* RIGHT: DASHBOARD SUMMARY (Conditionally Rendered) */}
          {hasSelection && (
            <div className="hidden lg:flex h-full flex-col justify-between rounded-3xl border border-white bg-white p-6 shadow-md animate-in fade-in zoom-in duration-300">
              <div>
                <div className="mb-4 flex items-center justify-between border-b border-slate-100 pb-4">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Your Scope</p>
                  <div className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_8px_#34d399]" />
                </div>
                
                <div className="space-y-3">
                  {selectedServices.map((svc, index) => {
                     const isPrimary = index === 0;
                     return (
                      <div 
                        key={svc} 
                        className={`group relative flex items-center justify-between rounded-xl border p-3 shadow-sm transition-all hover:shadow-md ${isPrimary ? 'border-amber-100 bg-amber-50/50' : 'border-slate-100 bg-white'}`}
                      >
                        <div className="flex flex-col">
                          <span className="font-bold text-slate-800 text-sm">
                             {getServiceLabel(svc)}
                          </span>
                          {isPrimary && <span className="text-[10px] text-amber-600 font-bold uppercase tracking-wider">Primary Scope</span>}
                        </div>
                        <button
                          type="button"
                          onClick={() => onToggleService(svc)}
                          className="flex h-6 w-6 items-center justify-center rounded-full text-slate-300 hover:bg-red-50 hover:text-red-500 transition-colors"
                          aria-label="Remove"
                        >
                          &times;
                        </button>
                      </div>
                     );
                  })}
                </div>
              </div>

              <div className="mt-6 rounded-2xl bg-emerald-50 p-5 border border-emerald-100">
                <div className="flex gap-3">
                   <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-200 text-emerald-700">
                     <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                   </div>
                   <div>
                     <p className="text-xs font-bold text-emerald-900 mb-1">Checklist Includes:</p>
                     <ul className="text-[10px] leading-relaxed text-emerald-800/80 list-disc ml-3 space-y-1">
                       {PLAN_CHECKLIST_ITEMS.map((item) => (
                         <li key={item}>{item}</li>
                       ))}
                     </ul>
                   </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function LoadingDots() {
  return (
    <span className="flex items-center gap-1">
      <span className="h-1 w-1 animate-bounce rounded-full bg-white" style={{ animationDelay: '0ms' }} />
      <span className="h-1 w-1 animate-bounce rounded-full bg-white" style={{ animationDelay: '120ms' }} />
      <span className="h-1 w-1 animate-bounce rounded-full bg-white" style={{ animationDelay: '240ms' }} />
    </span>
  );
}
